/**
 * Subscription plan catalog. Pure data + lookups, no Firebase.
 *
 * Commercial values (prices, quotas) can be overridden per deployment from the
 * `platform/billing` document; plan ids and the feature map live here so that
 * entitlement decisions never depend on remote data.
 */

export const PLAN_IDS = ['basic', 'advanced', 'platinum', 'founder'] as const;
export type PlanId = typeof PLAN_IDS[number];

export const BILLING_FEATURES = [
  'all_lottery_sources',
  'runner_map',
  'route_optimization',
  'surge_alerts',
  'analytics_export',
  'referrals',
  'email_templates',
  'direct_messages',
  'ai_assistant',
  'knowledge_base',
  'live_support',
  'branded_email',
  'data_export',
  'priority_sla',
] as const;
export type BillingFeature = typeof BILLING_FEATURES[number];

/** Seat or house quota meaning "no cap". */
export const UNLIMITED = -1;

/** Days of free trial for self-serve plans. */
export const TRIAL_DAYS = 7;

export interface PlanDefinition {
  id: PlanId;
  /** Monthly price in USD minor units (cents). */
  priceMinor: number;
  /** Price per house beyond `houseQuota`, in cents. */
  extraHousePriceMinor: number;
  houseQuota: number;
  seatQuota: number;
  auditRetentionDays: number;
  features: readonly BillingFeature[];
  trialDays: number;
  /** Founder is assigned by god only and never offered in self-serve checkout. */
  selfServe: boolean;
  /** Higher rank wins when comparing upgrades and downgrades. */
  rank: number;
}

const BASIC_FEATURES: readonly BillingFeature[] = [];

const ADVANCED_FEATURES: readonly BillingFeature[] = [
  'all_lottery_sources',
  'runner_map',
  'route_optimization',
  'surge_alerts',
  'analytics_export',
  'referrals',
  'email_templates',
  'direct_messages',
];

const PLATINUM_FEATURES: readonly BillingFeature[] = [
  ...ADVANCED_FEATURES,
  'ai_assistant',
  'knowledge_base',
  'live_support',
  'branded_email',
  'data_export',
  'priority_sla',
];

export const PLAN_CATALOG: Record<PlanId, PlanDefinition> = {
  basic: {
    id: 'basic',
    priceMinor: 3900,
    extraHousePriceMinor: 2500,
    houseQuota: 1,
    seatQuota: 8,
    auditRetentionDays: 30,
    features: BASIC_FEATURES,
    trialDays: TRIAL_DAYS,
    selfServe: true,
    rank: 1,
  },
  advanced: {
    id: 'advanced',
    priceMinor: 11900,
    extraHousePriceMinor: 2500,
    houseQuota: 3,
    seatQuota: 25,
    auditRetentionDays: 180,
    features: ADVANCED_FEATURES,
    trialDays: TRIAL_DAYS,
    selfServe: true,
    rank: 2,
  },
  platinum: {
    id: 'platinum',
    priceMinor: 29900,
    extraHousePriceMinor: 2500,
    houseQuota: 10,
    seatQuota: UNLIMITED,
    auditRetentionDays: 365,
    features: PLATINUM_FEATURES,
    trialDays: TRIAL_DAYS,
    selfServe: true,
    rank: 3,
  },
  founder: {
    id: 'founder',
    priceMinor: 7900,
    extraHousePriceMinor: 2500,
    houseQuota: 10,
    seatQuota: UNLIMITED,
    auditRetentionDays: 365,
    features: PLATINUM_FEATURES,
    trialDays: 0,
    selfServe: false,
    rank: 3,
  },
};

export const SELF_SERVE_PLAN_IDS: readonly PlanId[] = PLAN_IDS
  .filter((id) => PLAN_CATALOG[id].selfServe);

export function isPlanId(value: unknown): value is PlanId {
  return typeof value === 'string' && (PLAN_IDS as readonly string[]).includes(value);
}

export function planDefinition(planId: PlanId): PlanDefinition {
  return PLAN_CATALOG[planId];
}

/** Monthly total for a plan plus the houses billed beyond its quota. */
export function planPriceMinor(planId: PlanId, extraHouses = 0): number {
  const plan = PLAN_CATALOG[planId];
  const extra = Math.max(0, Math.trunc(extraHouses));
  return plan.priceMinor + extra * plan.extraHousePriceMinor;
}

/** Houses billed as extra for a given plan. */
export function extraHousesFor(planId: PlanId, houseCount: number): number {
  const quota = PLAN_CATALOG[planId].houseQuota;
  if (quota === UNLIMITED) return 0;
  return Math.max(0, Math.trunc(houseCount) - quota);
}

/** Positive when `a` outranks `b`; zero when the change is lateral. */
export function comparePlanRank(a: PlanId, b: PlanId): number {
  return PLAN_CATALOG[a].rank - PLAN_CATALOG[b].rank;
}

export function isUpgrade(from: PlanId, to: PlanId): boolean {
  return comparePlanRank(to, from) > 0;
}

export function isDowngrade(from: PlanId, to: PlanId): boolean {
  return comparePlanRank(to, from) < 0;
}
